<script setup lang="ts">
import { computed, onMounted, ref, toRef, watch } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { getSupportedLocales } from '@/plugins/i18n';

import LocalizedSelect from './base/LocalizedSelect.vue';
import HomepageItemSelectionTable from './HomepageItemSelectionTable.vue';

import { Constants } from '@/constants';
import { useMailingListStore } from '@/stores';
import { localizableString } from '@/helpers/validation.helper';
import type { Locales, LocalizableString, MailingListDto } from '@/services/types/api.types';
import { getTranslation, provideEditLocale } from '@/composables/data-display-utils';

// CONST
const { dialogRef, onDialogHide, onDialogCancel } = useDialogPluginComponent();
const { t } = useI18n();

// REF
const options = ref();
const parent = ref<number>();
const parent_id = ref<number>();
const type = ref<number>();
const error = ref<string | null>(null);
const itemType = ref<number>();

// STORE
const mailingListStore = useMailingListStore();

// PROPS
const props = defineProps<{
  title: string;
  languageEnabled: Locales[];
}>();

// EMITS
const emit = defineEmits(['ok', 'hide']);

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const { handleSubmit, values, setFieldValue } = useForm({
  validationSchema: yup.object({
    type: yup.string().required(),
    item: localizableString(props.languageEnabled, { nullable: true, optional: true }),
  }),
});

// LIFECYCLE
onMounted(async () => {
  options.value = mailingListStore.getMailingListOptions();
});

// COMPUTED
const hasItem = computed(() => {
  const item: LocalizableString = values.item;
  const locales = getSupportedLocales();
  let result = false;
  for (const locale of locales) {
    result ||= !!item?.[locale];
  }
  return result;
});

const itemName = computed(() => getTranslation(values, 'item', props.languageEnabled));

// FUNCTIONS
const changeMailingListFilter = async (value: number) => {
  switch (value) {
    case Constants.MailingList.DropdownType.CommunitySubscribers:
      parent.value = Constants.MailingList.Parent.community;
      parent_id.value = undefined;
      type.value = undefined;
      const data: MailingListDto = (await mailingListStore.fetchMailingListByParent(
        Constants.MailingList.Parent.community,
      ))!;
      setFieldValue('item', data.title_parent);
      break;
    case Constants.MailingList.DropdownType.ProjectSubscribers:
      parent.value = Constants.MailingList.Parent.project;
      type.value = Constants.MailingList.type.subscriber;
      itemType.value = Constants.HomepageItem.Type.Project;
      break;
    case Constants.MailingList.DropdownType.ConsultationParticipants:
      parent.value = Constants.MailingList.Parent.consultation;
      type.value = Constants.MailingList.type.participant;
      itemType.value = Constants.HomepageItem.Type.Consultation;
      break;
    case Constants.MailingList.DropdownType.ConsultationSubscribers:
      parent.value = Constants.MailingList.Parent.consultation;
      type.value = Constants.MailingList.type.subscriber;
      itemType.value = Constants.HomepageItem.Type.Consultation;
      break;
  }
};

const handleClear = () => {
  if (values.type === 1 && hasItem.value) {
    setFieldValue('type', null);
  }
  resetItem();
};

const resetItem = () => {
  error.value = null;
  setFieldValue('item', null);
};

const onSubmit = handleSubmit(async () => {
  if (!parent.value) return;
  const result = await mailingListStore.fetchMailingListByParent(parent.value, parent_id.value, type.value);
  if (!result) {
    error.value = t('global.notifications.no-related-mailingList');
    return;
  }
  emit('ok', result);
  dialogRef.value?.hide();
});

function selectHomepageItem(value: any) {
  parent_id.value = value.id;
  setFieldValue('item', value.title);
}

// WATCH
watch(
  () => values.type,
  async (newValue: number | null) => {
    if (newValue) {
      resetItem();
      await changeMailingListFilter(newValue);
    }
  },
);
</script>
<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" class="mailing-list-modal">
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ props.title }}</div>
        <q-space />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-select
          name="type"
          :label="t('global.label.type')"
          :editLocale="editLocale"
          :options="options"
          emit-value
          map-options
          outlined
        />
        <div class="item">
          <q-input
            v-if="hasItem"
            v-model="itemName"
            name="item"
            :label="t('campaign.mailingLists.label')"
            outlined
            clearable
            @clear="handleClear"
          />
        </div>
        <homepage-item-selection-table
          v-if="
            values.type &&
            values.type != Constants.MailingList.DropdownType.CommunitySubscribers &&
            itemType &&
            !hasItem
          "
          :item-type="itemType"
          :language-enabled="props.languageEnabled"
          @row-click="selectHomepageItem"
        />
        <q-banner v-if="error" class="bg-white text-red q-mt-sm">
          {{ error }}
        </q-banner>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<style lang="scss">
.mailing-list-modal {
  .item {
    display: flex;
    flex-direction: row;
    gap: 10px;
    width: 100%;

    .q-field {
      width: inherit;
    }
  }
}
</style>
